// React
import React from "react";
import { useForm } from "react-hook-form";

// Libraries
import { AlertCircle } from "lucide-react";
import Mustache from "mustache";

// Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Props = {
  dynamicFormInputs: {
    promptTitle: string;
    prompt: string;
    dynamicInputs: string[] | null;
  };
  onOpen: boolean;
  onClose: (value: boolean) => void;
  promptSubmitHandler: (
    prompt_title: string,
    prompt: string,
    keywords: any
  ) => void;
};

function PromptDialogue({
  onOpen,
  onClose,
  dynamicFormInputs,
  promptSubmitHandler,
}: Props) {
  const closeHandler = () => {
    onClose(!onOpen);
    reset();
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = (data: any) => {
    const output = Mustache.render(
      dynamicFormInputs.prompt.replace(/\s*{{\s*(.*?)\s*}}\s*/g, "{{ $1 }}"),
      data
    );
    promptSubmitHandler(dynamicFormInputs.promptTitle, output, data);
    reset();
    closeHandler();
  };

  return (
    <div
      className={`fixed bottom-40 max-sm:bottom-44 left-1/2 transform -translate-x-1/2 
        bg-[#27272A] border border-[#303030] shadow-md text-foreground p-6 max-w-[90vw] 
        w-full sm:w-[400px] rounded-xl transition-all duration-500 ease-in-out 
        ${onOpen ? "opacity-100 translate-y-0" : "opacity-0 pointer-events-none translate-y-4"}`}
    >
      <h1 className="text-xl font-bold text-foreground">
        Please fill in the details
      </h1>
      <h2 className="text-muted-foreground font-medium mt-3">
        {" "}
        {"Q. " + dynamicFormInputs?.promptTitle}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
        {dynamicFormInputs?.dynamicInputs?.map((item, index) => (
          <div key={index}>
            <label
              htmlFor={item}
              className="block text-muted-foreground font-medium mb-1"
            >
              Enter {item}
            </label>
            <Input
              className="bg-white text-black border border-border focus:ring-ring focus:border-ring rounded-md px-3 py-2"
              id={item}
              {...register(item, { required: true })}
            />
            {errors[item] && (
              <div className="flex items-center gap-2 mt-1 text-sm text-destructive">
                <AlertCircle size={15} />
                <p>{item} cannot be blank</p>
              </div>
            )}
          </div>
        ))}

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            onClick={closeHandler}
            variant="outline"
            className="px-4 py-2"
          >
            Cancel
          </Button>
          <Button type="submit" className="px-4 py-2">
            Done
          </Button>
        </div>
      </form>
    </div>
  );
}

export default PromptDialogue;
